import React from 'react'

export interface GradientBorderProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Border radius for the gradient border
   * @default "1rem"
   */
  borderRadius?: string

  /**
   * Width of the gradient border (padding)
   * @default "3px"
   */
  borderWidth?: string

  /**
   * Background color of the inner content area
   * Uses theme-aware CSS variables by default
   * @default undefined (uses theme background)
   */
  innerBackground?: string

  children: React.ReactNode
}

/**
 * A flexible gradient border container that behaves like a div but with
 * a gradient border effect. Supports theme-aware backgrounds and
 * configurable border radius and width.
 */
export const GradientBorder = React.forwardRef<
  HTMLDivElement,
  GradientBorderProps
>(
  (
    {
      borderRadius = '1rem',
      borderWidth = '3px',
      innerBackground,
      className = '',
      style,
      children,
      ...divProps
    },
    ref
  ) => {
    const outerStyle: React.CSSProperties = {
      background: 'var(--ai-gradient)',
      padding: borderWidth,
      borderRadius,
      ...style,
    }

    const innerStyle: React.CSSProperties = {
      borderRadius: `calc(${borderRadius} - ${borderWidth})`,
      position: 'relative',
      ...(innerBackground ? { background: innerBackground } : {}),
    }

    return (
      <div
        ref={ref}
        className={`gradient-border-outer ${className}`}
        style={outerStyle}
        {...divProps}
      >
        <div className="gradient-border-inner" style={innerStyle}>
          {children}
        </div>
      </div>
    )
  }
)

GradientBorder.displayName = 'GradientBorder'
